import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Role,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useGetRolesQuery,
  useUpdateRoleMutation,
} from "../api/accessApi";

export default function RolesListPage() {
  const navigate = useNavigate();
  const { data = [], isLoading, error, refetch } = useGetRolesQuery();

  const [createRole, { isLoading: creating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: updating }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: deleting }] = useDeleteRoleMutation();

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState<Role | null>(null);

  const [name, setName] = useState("");
  const [editName, setEditName] = useState("");

  const rolesSorted = useMemo(() => {
    return [...data].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const onAdd = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await createRole({ name: n }).unwrap();
      setName("");
      setOpenAdd(false);
    } catch {}
  };

  const onEdit = async () => {
    if (!openEdit) return;
    const n = editName.trim();
    if (!n) return;
    try {
      await updateRole({ id: openEdit.id, name: n }).unwrap();
      setOpenEdit(null);
    } catch {}
  };

  const onDelete = async (role: Role) => {
    const ok = window.confirm(`Delete role "${role.name}"?`);
    if (!ok) return;
    try {
      await deleteRole(role.id).unwrap();
    } catch {}
  };

  return (
    <div className="bg-white border rounded-xl p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Roles</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create roles, view details, and assign permissions.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Refresh
          </button>

          <button
            onClick={() => setOpenAdd(true)}
            className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm"
          >
            + Add Role
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-6 text-gray-600">Loading roles...</div>
      ) : error ? (
        <div className="mt-6 text-red-600">
          Failed to load roles. Check your API and token.
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Role</th>
                <th className="py-2">Guard</th>
                <th className="py-2 w-[240px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rolesSorted.map((role) => (
                <tr key={role.id} className="border-b">
                  <td className="py-3 font-semibold text-gray-800">
                    {role.name}
                  </td>
                  <td className="py-3 text-gray-600">{role.guard_name}</td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(`/access/roles/${role.id}`)}
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                      >
                        View
                      </button>

                      <button
                        onClick={() => {
                          setOpenEdit(role);
                          setEditName(role.name);
                        }}
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => onDelete(role)}
                        disabled={deleting}
                        className="px-3 py-1.5 rounded-lg border text-red-700 hover:bg-red-50 text-xs disabled:opacity-60"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!rolesSorted.length ? (
                <tr>
                  <td className="py-6 text-gray-500" colSpan={3}>
                    No roles found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Modal */}
      {openAdd ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white border shadow-lg p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Add Role</h2>
                <p className="text-sm text-gray-500 mt-1">Create a new role.</p>
              </div>

              <button
                onClick={() => setOpenAdd(false)}
                className="px-2 py-1 rounded border hover:bg-gray-50"
              >
                ✕
              </button>
            </div>

            <div className="mt-4">
              <label className="text-sm text-gray-600">Role name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-gray-200"
                placeholder="e.g. admin"
              />
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setOpenAdd(false)}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={onAdd}
                disabled={creating || !name.trim()}
                className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
              >
                {creating ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Edit Modal */}
      {openEdit ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white border shadow-lg p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Edit Role</h2>
                <p className="text-sm text-gray-500 mt-1">Rename role.</p>
              </div>

              <button
                onClick={() => setOpenEdit(null)}
                className="px-2 py-1 rounded border hover:bg-gray-50"
              >
                ✕
              </button>
            </div>

            <div className="mt-4">
              <label className="text-sm text-gray-600">Role name</label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-gray-200"
              />
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setOpenEdit(null)}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={onEdit}
                disabled={updating || !editName.trim()}
                className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
              >
                {updating ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
